import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_URL, Artifact } from '../../types';
import { useTheme } from '../../ThemeContext';
import { fetchJobArtifacts, downloadArtifact } from '../../utils/artifactsService';
import { Download } from 'lucide-react';
import {apiGet, apiPatch} from "../../utils/apiClient";

interface JobDetails {
  job_id: string;
  pipeline_id: string;
  pipeline_version?: string;
  status: string;
  started_at?: string;
  completed_at?: string;
  records_processed?: number | null;
  error_message?: string | null;
}

export default function JobDetailsPage() {
  const { theme } = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState<JobDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [logs, setLogs] = useState<{ stdout?: string | null; status?: string } | null>(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);

  const [overriding, setOverriding] = useState(false);
  const [overrideError, setOverrideError] = useState<string | null>(null);

  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [artifactsLoading, setArtifactsLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      setLoading(true);
      setError(null);
      try {
        const res = await apiGet(`${API_URL}/api/jobs/${id}`);
        if (!res.ok) throw new Error('Job not found');
        const data = await res.json();
        setJob(data);
      } catch (e: any) {
        setError(e?.message || 'Failed to load job');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const loadLogs = async () => {
    if (!id) return;
    setLogsLoading(true);
    setLogsError(null);
    try {
      const res = await apiGet(`${API_URL}/api/jobs/${id}/logs`);
      if (!res.ok) throw new Error('Failed to fetch logs');
      const data = await res.json();
      setLogs({ stdout: data?.logs?.stdout ?? null, status: data?.status });
    } catch (e: any) {
      setLogsError(e?.message || 'Could not load logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const loadArtifacts = async () => {
    if (!id) return;
    setArtifactsLoading(true);
    try {
      const data = await fetchJobArtifacts(id);
      setArtifacts(data.artifacts || []);
    } catch (e: any) {
      // Silently fail - not all jobs have artifacts
      setArtifacts([]);
    } finally {
      setArtifactsLoading(false);
    }
  };

  const handleOverrideStatus = async () => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to manually mark this job as failed?')) return;
    
    setOverriding(true);
    setOverrideError(null);
    try {
      const res = await apiPatch(`${API_URL}/api/jobs/${id}/override-status`);
      if (!res.ok) throw new Error('Failed to override job status');
      const data = await res.json();
      setJob(data);
    } catch (e: any) {
      setOverrideError(e?.message || 'Could not override job status');
    } finally {
      setOverriding(false);
    }
  };

  useEffect(() => {
    loadLogs();
    loadArtifacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) return <div style={{ color: theme.text }}>Loading...</div>;
  if (error) return (
    <div>
      <div style={{ background: theme.errorLight, color: theme.error, border: `1px solid ${theme.error}`, padding: 12, borderRadius: 8, marginBottom: 12 }}>{error}</div>
      <button onClick={() => navigate(-1)} style={{ border: `1px solid ${theme.border}`, background: theme.cardBackground, color: theme.text, borderRadius: 8, padding: '8px 12px' }}>Back</button>
    </div>
  );
  if (!job) return null;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, color: theme.text }}>Job Details</h2>
          <p style={{ margin: '6px 0 0 0', color: theme.textSecondary }}>Inspect job metadata and logs</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => navigate(-1)} style={{ border: `1px solid ${theme.border}`, background: theme.cardBackground, color: theme.text, borderRadius: 8, padding: '8px 12px' }}>Back</button>
          <button onClick={loadLogs} style={{ border: `1px solid ${theme.border}`, background: theme.cardBackground, color: theme.text, borderRadius: 8, padding: '8px 12px' }}>Refresh logs</button>
        </div>
      </div>

      <div style={{ background: theme.cardBackground, border: `1px solid ${theme.border}`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', rowGap: 10 }}>
          <div style={{ color: theme.textSecondary }}>Job ID</div>
          <div style={{ fontWeight: 600, color: theme.text }}>{job.job_id}</div>

          <div style={{ color: theme.textSecondary }}>Pipeline ID</div>
          <div style={{ fontWeight: 600, color: theme.text }}>{job.pipeline_id}</div>

          <div style={{ color: theme.textSecondary }}>Pipeline Version</div>
          <div style={{ fontWeight: 600, color: theme.text }}>
            {job.pipeline_version ? (
              <>
                {`v${job.pipeline_version}`} {" "}
                <a
                  href={`/pipelines/${job.pipeline_id}?version=${encodeURIComponent(job.pipeline_version || '')}`}
                  style={{ marginLeft: 8, fontWeight: 500, textDecoration: 'none', color: theme.info }}
                  title="Open pipeline at this version"
                >
                  View pipeline
                </a>
              </>
            ) : (
              '-'
            )}
          </div>

          <div style={{ color: theme.textSecondary }}>Status</div>
          <div>
            <span style={{ padding: '2px 8px', background: theme.hoverBackground, color: theme.text, borderRadius: 12 }}>{job.status}</span>
            {job.status !== 'failed' && job.status !== 'completed' && (
              <button 
                onClick={handleOverrideStatus} 
                disabled={overriding}
                style={{ 
                  marginLeft: 12, 
                  border: `1px solid ${theme.error}`, 
                  background: theme.errorLight, 
                  color: theme.error,
                  borderRadius: 8, 
                  padding: '4px 10px',
                  cursor: overriding ? 'not-allowed' : 'pointer',
                  opacity: overriding ? 0.6 : 1
                }}
                title="Manually mark this job as failed"
              >
                {overriding ? 'Overriding...' : 'Mark as Failed'}
              </button>
            )}
            {overrideError && (
              <div style={{ 
                marginTop: 8, 
                background: theme.errorLight, 
                color: theme.error, 
                border: `1px solid ${theme.error}`, 
                padding: 8, 
                borderRadius: 8,
                fontSize: '0.9em'
              }}>
                {overrideError}
              </div>
            )}
          </div>

          <div style={{ color: theme.textSecondary }}>Started</div>
          <div style={{ color: theme.text }}>{job.started_at ? new Date(job.started_at).toLocaleString() : '-'}</div>

          <div style={{ color: theme.textSecondary }}>Completed</div>
          <div style={{ color: theme.text }}>{job.completed_at ? new Date(job.completed_at).toLocaleString() : '-'}</div>

          <div style={{ color: theme.textSecondary }}>Records Processed</div>
          <div style={{ color: theme.text }}>{job.records_processed ?? '-'}</div>

          {job.error_message && (
            <>
              <div style={{ color: theme.textSecondary }}>Error</div>
              <div style={{ color: theme.error }}>{job.error_message}</div>
            </>
          )}
        </div>
      </div>

      {/* Artifacts Section */}
      {!artifactsLoading && artifacts.length > 0 && (
        <div style={{ background: theme.cardBackground, border: `1px solid ${theme.border}`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
          <h3 style={{ margin: '0 0 12px 0', color: theme.text, fontSize: 18 }}>Artifacts</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {artifacts.map((artifact, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: 12,
                  background: theme.background,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 8,
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: theme.text, marginBottom: 4 }}>
                    {artifact.name}
                  </div>
                  {artifact.description && (
                    <div style={{ fontSize: 13, color: theme.textSecondary, marginBottom: 4 }}>
                      {artifact.description}
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: theme.textSecondary }}>
                    {artifact.is_directory ? 'Directory' : 'File'}
                    {artifact.size && ` • ${(artifact.size / 1024).toFixed(2)} KB`}
                  </div>
                </div>
                {!artifact.is_directory && (
                  <button
                    onClick={async () => {
                      try {
                        await downloadArtifact(job.job_id, artifact.path);
                      } catch (error) {
                        console.error('Failed to download artifact:', error);
                        alert('Failed to download artifact. Please try again.');
                      }
                    }}
                    style={{
                      padding: '8px 16px',
                      background: theme.buttonBackground,
                      color: theme.buttonText,
                      border: 'none',
                      borderRadius: 6,
                      cursor: 'pointer',
                      textDecoration: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontWeight: 600,
                      fontSize: 14,
                    }}
                  >
                    <Download size={16} />
                    Download
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ background: theme.cardBackground, border: `1px solid ${theme.border}`, borderRadius: 12, padding: 12 }}>
        <div style={{ fontWeight: 700, marginBottom: 6, color: theme.text }}>Pipeline Logs</div>
        {logsLoading ? (
          <div style={{ color: theme.textSecondary }}>Loading…</div>
        ) : logsError ? (
          <div style={{ background: theme.errorLight, color: theme.error, border: `1px solid ${theme.error}`, padding: 10, borderRadius: 8 }}>{logsError}</div>
        ) : (
          <pre style={{ background: theme.tableHeaderBg, color: theme.text, padding: 12, borderRadius: 8, maxHeight: '60vh', overflow: 'auto', whiteSpace: 'pre-wrap' }}>{logs?.stdout || '—'}</pre>
        )}
      </div>
    </div>
  );
}

